import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Save, AlertTriangle, CheckCircle2 } from "lucide-react";
import api, { getCrewToken, getCrewInfo, clearCrewSession } from "@/lib/api";
import { toast } from "sonner";
import StaffSignInForm from "@/pages/StaffSignInForm";

const CATEGORY_STYLES = {
  orientation: { tag: "Orientation", color: "bg-violet-100 text-violet-800" },
  meet: { tag: "Meet Your Mateys", color: "bg-teal-100 text-teal-800" },
  camp: { tag: "Camp", color: "bg-blue-100 text-blue-800" },
};

function fmtPretty(dateStr) {
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
}
function fmtToday() {
  return new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
}
function fmtClock(d) {
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

export default function StaffAbsenceForm() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(!!getCrewToken());
  const [info, setInfo] = useState(null);

  // Planned absence
  const [formDates, setFormDates] = useState([]);
  const [absent, setAbsent] = useState({});
  const [reasons, setReasons] = useState({});
  const [loadingForm, setLoadingForm] = useState(true);
  const [savingForm, setSavingForm] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);

  const [now, setNow] = useState(new Date());
  useEffect(() => { const id = setInterval(() => setNow(new Date()), 30000); return () => clearInterval(id); }, []);

  useEffect(() => {
    if (!authed) return;
    setInfo(getCrewInfo());
    refreshAll();
  }, [authed]);

  const refreshAll = async () => {
    setLoadingForm(true);
    try {
      const [fd, me] = await Promise.all([
        api.get("/crew/form-dates"),
        api.get("/crew/me"),
      ]);
      setFormDates(fd.data.items || []);
      const a = {}, r = {};
      (me.data.absences || []).forEach((row) => { a[row.date] = true; r[row.date] = row.reason || ""; });
      setAbsent(a);
      setReasons(r);
      setAlreadySubmitted(!!me.data.crew?.absence_form_submitted_at_utc);
    } catch (e) {
      toast.error("Could not load");
    } finally {
      setLoadingForm(false);
    }
  };

  const sections = useMemo(() => {
    const groups = { orientation: [], meet: [], camp: [] };
    formDates.forEach((d) => { (groups[d.category] || groups.camp).push(d); });
    return groups;
  }, [formDates]);

  const selectedDates = useMemo(() => Object.keys(absent).filter((d) => absent[d]).sort(), [absent]);

  const longRunWarning = useMemo(() => {
    const camps = formDates.filter((d) => d.category === "camp").map((d) => d.date);
    let cur = 0, max = 0;
    for (const d of camps) {
      if (absent[d]) { cur += 1; max = Math.max(max, cur); } else { cur = 0; }
    }
    return max >= 2;
  }, [absent, formDates]);

  const submitForm = async () => {
    if (alreadySubmitted) {
      toast.message("You've already submitted today. Contact a director to make changes.");
      return;
    }
    setSavingForm(true);
    try {
      const payload = selectedDates.map((d) => ({ date: d, reason: (reasons[d] || "").trim() }));
      const res = await api.post("/crew/planned-absence", { absences: payload });
      toast.success(`Saved ${res.data.count} planned absence(s).`);
      setAlreadySubmitted(true);
      setShowConfirm(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally { setSavingForm(false); }
  };

  const checkIn = async () => {
    /* deprecated — sign-in/out are handled at the kiosk only */
  };
  const checkOut = async () => {
    /* deprecated — sign-in/out are handled at the kiosk only */
  };

  const doneReturnToLock = () => {
    clearCrewSession();
    navigate("/");
  };

  const logout = () => { clearCrewSession(); setAuthed(false); navigate("/"); };

  if (!authed) {
    return <StaffSignInForm onSignedIn={() => setAuthed(true)} onBack={() => navigate("/")} />;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Rolling River Day Camp" className="w-10 h-10 rounded-xl object-contain bg-white shadow-sm p-0.5" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Rolling River — Staff Portal</div>
              <h1 className="font-display text-base font-bold text-slate-900">Hi, {info?.name || "Staff"}</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="font-mono tabular-nums text-sm font-semibold text-slate-900">{fmtClock(now)}</div>
              <div className="text-[10px] text-slate-500">{fmtToday()}</div>
            </div>
            <button onClick={doneReturnToLock} className="btn-touch inline-flex items-center gap-1 px-3 rounded-lg text-white bg-emerald-600 hover:bg-emerald-700 text-sm font-semibold" data-testid="done-next-person">
              Done — Next Person →
            </button>
            <button onClick={logout} className="btn-touch inline-flex items-center gap-1 px-3 rounded-lg text-slate-600 hover:text-red-700 hover:bg-red-50 text-sm font-semibold" data-testid="staff-logout">
              <LogOut className="w-4 h-4" /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Planned Absence form */}
        <section className="rounded-2xl bg-white border border-slate-200 p-5 sm:p-6" data-testid="absence-form-section">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h2 className="font-display text-lg font-bold text-slate-900">Planned Absences (2026 Season)</h2>
            <span className="text-sm font-semibold text-slate-900 tabular-nums" data-testid="selected-counter">
              {selectedDates.length} day{selectedDates.length === 1 ? "" : "s"} marked Absent
            </span>
          </div>

          {alreadySubmitted && (
            <div className="mb-4 px-4 py-3 rounded-xl bg-emerald-50 border border-emerald-300 text-emerald-900 text-sm flex items-start gap-2" data-testid="already-submitted-banner">
              <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-bold">You've already submitted this form.</div>
                <div className="text-emerald-800/90">Contact a director (<a className="underline" href="mailto:colleen@rollingriver.com">colleen@rollingriver.com</a>) if you need to make a change.</div>
              </div>
            </div>
          )}

          <div className="text-slate-700 text-sm leading-relaxed space-y-2 mb-4 px-4 py-3 bg-slate-50 rounded-xl border border-slate-100">
            <p>You're expected to work all <strong>38 days of camp</strong>. Absences <strong>more than one day must be approved by the Directors</strong> in advance.</p>
            <p className="text-slate-600">Unplanned same-day absences (illness, etc.): email <a href="mailto:colleen@rollingriver.com" className="text-blue-700 underline">colleen@rollingriver.com</a>.</p>
          </div>

          {longRunWarning && (
            <div className="mb-4 px-4 py-3 rounded-xl bg-amber-50 border border-amber-300 text-amber-900 text-sm flex items-start gap-2" data-testid="long-run-warning">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>You're requesting <strong>2+ consecutive camp days</strong> off — confirm director approval before submitting.</div>
            </div>
          )}

          {loadingForm ? (
            <div className="text-slate-500 text-sm">Loading form…</div>
          ) : (
            <>
              {["orientation", "meet", "camp"].map((cat) => (
                sections[cat]?.length > 0 && (
                  <div key={cat} className="mb-5" data-testid={`section-${cat}`}>
                    <h3 className="font-display text-xs font-bold text-slate-500 uppercase tracking-[0.2em] mb-2">
                      {cat === "orientation" ? "Orientation" : cat === "meet" ? "Meet Your Mateys Day" : "Camp Days"}
                    </h3>
                    <div className="space-y-2">
                      {sections[cat].map((item) => {
                        const sty = CATEGORY_STYLES[item.category] || CATEGORY_STYLES.camp;
                        const yes = !!absent[item.date];
                        return (
                          <div key={item.date} className={`rounded-xl border ${yes ? "border-amber-300 bg-amber-50/40" : "border-slate-200 bg-white"} p-3 sm:p-4`} data-testid={`absence-row-${item.date}`}>
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                              <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.16em] ${sty.color}`}>{sty.tag}</span>
                                  {item.note && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.16em] bg-blue-100 text-blue-800">{item.note}</span>}
                                </div>
                                <div className="font-display text-base font-bold text-slate-900 mt-1.5">{item.label}</div>
                                <div className="text-xs text-slate-500 tabular-nums">{fmtPretty(item.date)}</div>
                              </div>
                              <div className="flex items-center gap-2">
                                <label className={`btn-touch px-3 rounded-lg border cursor-pointer text-sm font-semibold ${!yes ? "bg-emerald-600 text-white border-emerald-700" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"} ${alreadySubmitted ? "opacity-60 cursor-not-allowed pointer-events-none" : ""}`}>
                                  <input
                                    type="radio" name={`a-${item.date}`} checked={!yes}
                                    disabled={alreadySubmitted}
                                    onChange={() => { const a = { ...absent }; delete a[item.date]; setAbsent(a); const r = { ...reasons }; delete r[item.date]; setReasons(r); }}
                                    className="sr-only" data-testid={`no-${item.date}`}
                                  />
                                  Present
                                </label>
                                <label className={`btn-touch px-3 rounded-lg border cursor-pointer text-sm font-semibold ${yes ? "bg-red-600 text-white border-red-700" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"} ${alreadySubmitted ? "opacity-60 cursor-not-allowed pointer-events-none" : ""}`}>
                                  <input
                                    type="radio" name={`a-${item.date}`} checked={yes}
                                    disabled={alreadySubmitted}
                                    onChange={() => setAbsent({ ...absent, [item.date]: true })}
                                    className="sr-only" data-testid={`yes-${item.date}`}
                                  />
                                  Absent
                                </label>
                              </div>
                            </div>
                            {yes && (
                              <div className="mt-2">
                                <input
                                  type="text"
                                  value={reasons[item.date] || ""}
                                  disabled={alreadySubmitted}
                                  onChange={(e) => setReasons({ ...reasons, [item.date]: e.target.value })}
                                  placeholder="Why are you requesting this day off?"
                                  className={`w-full px-3 py-2 rounded-lg border border-slate-200 outline-none focus:ring-2 focus:ring-blue-400 text-sm ${alreadySubmitted ? "bg-slate-50 text-slate-500 cursor-not-allowed" : ""}`}
                                  data-testid={`reason-${item.date}`}
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )
              ))}

              <button
                onClick={submitForm}
                disabled={savingForm || alreadySubmitted}
                className="w-full btn-touch inline-flex items-center justify-center gap-2 px-5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-absences"
              >
                <Save className="w-4 h-4" />
                {alreadySubmitted ? "Already Submitted" :
                 savingForm ? "Saving…" :
                 selectedDates.length === 0 ? "Submit (no absences)" :
                 `Submit ${selectedDates.length} Planned Absence${selectedDates.length === 1 ? "" : "s"}`}
              </button>
            </>
          )}
        </section>
      </main>

      {showConfirm && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-slate-200 p-6 text-center">
            <div className="w-14 h-14 rounded-full bg-emerald-100 grid place-items-center mx-auto mb-3"><CheckCircle2 className="w-7 h-7 text-emerald-600" /></div>
            <h3 className="font-display text-xl font-bold mb-2">Saved!</h3>
            <p className="text-sm text-slate-600 mb-4">Your planned absences have been recorded for {info?.name || "you"}.</p>
            <button onClick={() => setShowConfirm(false)} className="w-full btn-touch px-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold" data-testid="confirm-close">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
